import { Body, Heading } from '@assembly-js/design-system';
import { SvgIconProps } from '@mui/material';
import clsx from 'clsx';
import React, { ComponentType } from 'react';
import Stripe from 'stripe';

import { ForwardIcon } from 'src/components/icons';
import { ACHDisabledTooltip } from 'src/features/payments/components/ACHDisabledTooltip';

export interface PaymentMethodOptionProps {
  title: string;
  description: string;
  feeAmount: string;
  Icon: ComponentType<SvgIconProps>;
  onPaymentMethodOptionSelected: (
    createdSource?: Stripe.CustomerSource,
  ) => void;
  isMobileView?: boolean;
  previewMode?: boolean;
  disabled?: boolean;
  /** When true with disabled, shows the shared ACH-capability tooltip. */
  showACHDisabledTooltip?: boolean;
}

export const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = ({
  title,
  description,
  feeAmount,
  Icon,
  onPaymentMethodOptionSelected,
  isMobileView = false,
  previewMode = false,
  disabled = false,
  showACHDisabledTooltip = false,
}) => {
  const mobileView = isMobileView || previewMode;

  const handlePaymentMethodOptionSelect = () => {
    if (!previewMode && !disabled) {
      onPaymentMethodOptionSelected();
    }
  };

  const option = (
    <div
      className={clsx(
        'flex items-center py-3 px-5 border border-solid border-gray-150 mt-4 bg-transparent max-w-[500px] rounded',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:cursor-pointer',
      )}
      onClick={handlePaymentMethodOptionSelect}
    >
      <div
        className={clsx(
          'flex items-center justify-center flex-shrink-0 rounded-full  bg-[var(--primary-color)] text-white',
          {
            'h-10 w-10': !mobileView,
            'h-[26px] w-[26px]': mobileView,
          },
        )}
      >
        <Icon
          fill="white"
          className={clsx('text-white', {
            'text-base': mobileView,
            'text-xl': !mobileView,
          })}
        />
      </div>
      <div
        className={clsx('px-5 grow', {
          'border border-r border-y-0 border-l-0 border-solid border-gray-150':
            Boolean(feeAmount),
        })}
      >
        <Heading
          className={clsx('text-black-heading tracking-[-0.05px]', {
            'text-[12px]': mobileView,
            'text-[15px] leading-6': !mobileView,
          })}
        >
          {title}
        </Heading>
        <Body
          className={clsx('text-secondary tracking-[-0.05px]', {
            'text-[10px] leading-[12px] ': mobileView,
            'text-[12px] leading-[18px] y-500': !mobileView,
          })}
        >
          {description}
        </Body>
      </div>

      {feeAmount && (
        <div
          className={clsx('text-right pr-2', {
            'min-w-[45px]': mobileView,
            'min-w-[80px]': !mobileView,
          })}
        >
          <Body
            className={clsx('text-secondary', {
              'text-[10px] leading-[12px] ': mobileView,
              'text-[12px] leading-4 y-500': !mobileView,
            })}
          >
            Fee {feeAmount}
          </Body>
        </div>
      )}
      {/* <div className="flex-shrink-0"> */}
      <ForwardIcon style={{ fontSize: isMobileView ? 10 : 15 }} />
      {/* </div> */}
    </div>
  );

  if (disabled && showACHDisabledTooltip) {
    return (
      <ACHDisabledTooltip>
        <div>{option}</div>
      </ACHDisabledTooltip>
    );
  }

  return option;
};
